// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@lfx/shared/shared.module';
import { ButtonComponent } from '@lfx/shared/components/button/button.component';
import { KeyProjectsHelper } from '@lfx/key-projects/key-projects.helper';

export interface HealthMetric {
  label: string;
  percent: number;
  value: number;
  bgColor: string;
}

export interface HealthScoreData {
  metrics: HealthMetric[];
  description: string;
  isLoading: boolean;
  error: string | null;
}

@Component({
  selector: 'lfx-health-score-popover-content',
  imports: [CommonModule, SharedModule, ButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="health-score-details">
      <!-- Header with title and LFX Insights button -->
      <div class="health-score-header">
        <h4 class="health-score-title lf-body-bold-sm">Health score</h4>
        <lfx-button
          variant="tertiary"
          size="small"
          svgIcon="assets/img/svgs/arrow-up-right.svg"
          iconPosition="left"
          (clicked)="openLfxInsights()">
          LFX Insights
        </lfx-button>
      </div>

      <!-- Health status with dot and text -->
      <div class="health-status">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" [attr.fill]="dotColor()" stroke="white" stroke-width="4"/>
        </svg>
        <span class="health-status-text health-status-value">{{ displayText() }}</span>
      </div>

      <!-- Description -->
      <p class="health-description">
        {{ data()?.description || '' | ellipsis:92 }}
      </p>

      <!-- Loading state -->
      @if (data()?.isLoading) {
        <div class="health-loading">
          <i class="fal fa-spinner-third fa-spin"></i>
          <span class="loading-text">Loading detailed metrics...</span>
        </div>
      }

      <!-- Error state -->
      @if (data()?.error) {
        <div class="health-error">
          <i class="fal fa-exclamation-triangle"></i>
          <span class="error-text">{{ data()?.error }}</span>
        </div>
      }

      <!-- Metrics with progress bars -->
      @if (!data()?.isLoading && !data()?.error && data()?.metrics) {
        <div class="health-metrics">
          @for (metric of data()?.metrics; track metric.label) {
            <div class="metric-item">
              <span class="metric-label lf-body-bold-sm">{{ metric.label }}</span>
              <lfx-percentage-bar
                [sections]="getMetricSections(metric)"
                [height]="4"
                [hasBorderRadius]="true"
                [showTooltip]="false">
              </lfx-percentage-bar>
            </div>
          }
        </div>
      }
    </div>
  `,
  styleUrls: ['../health-score-column.component.scss']
})
export class HealthScorePopoverContentComponent {
  // Input properties
  readonly variant = input.required<string>();
  readonly score = input<number | null>(null);
  readonly customText = input<string>('');
  readonly projectSlug = input<string>('');
  readonly data = input<HealthScoreData | null>(null);

  protected readonly displayText = computed(() => {
    if (this.customText()) {
      return this.customText();
    }

    if (this.score() !== null) {
      return this.score()?.toString();
    }

    // Use centralized labels
    return KeyProjectsHelper.getHealthScoreDisplayText(this.variant());
  });

  protected readonly dotColor = computed(() => {
    return KeyProjectsHelper.getHealthScoreColor(this.variant(), 'primary');
  });

  protected readonly insightsUrl = computed(() => {
    const slug = this.projectSlug();
    return slug ? `https://insights.linuxfoundation.org/project/${slug}` : '#';
  });

  protected openLfxInsights(): void {
    const url = this.insightsUrl();
    if (url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

    protected getMetricSections(metric: HealthMetric) {
    return [
      {
        name: metric.label,
        percent: metric.percent,
        value: metric.value,
        bgColor: metric.bgColor // Use metric's bgColor for active portion
      },
      {
        name: 'Remaining',
        percent: 100 - metric.percent,
        value: 100 - metric.value,
        bgColor: KeyProjectsHelper.getPlaceholderColor() // Fixed gray color for remaining
      }
    ];
  }
}
